import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { RestApiService } from '../../core/services/rest-api.service';
import { PipedriveFlow } from '../models/pipedrive.models';

// Raised when a Pipedrive call is attempted but the integration was never configured.
export class PipedriveNotConfiguredError extends Error {
  constructor() {
    super('PIPEDRIVE_NOT_CONFIGURED');
    this.name = 'PipedriveNotConfiguredError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class PipedriveService {

  baseUrl: string = '';
  apiPath: string = '';
  apiToken: string = '';
  licenseFlowId: string = '';
  hostFlowId: string = '';
  isConfigured: boolean = false;

  private ready$ = new ReplaySubject<void>(1);

  constructor(private http: HttpClient,
              private restApiService: RestApiService) {
    this.loadConfig();
  }

  /**
   * (Re)carrega a configuração do Pipedrive a partir do backend.
   * Pode ser chamado após salvar a config para atualizar o serviço sem recarregar a página.
   */
  public loadConfig(): void {
    // Reinicia o gate para refletir o novo estado de carregamento.
    this.ready$ = new ReplaySubject<void>(1);

    this.restApiService.get('/pipedrive').subscribe({
      next: (response) => {
        let data = JSON.parse(response.data);

        this.baseUrl = data.baseUrl ?? this.deriveBaseUrl(data.urlApi);
        this.apiPath = data.apiPath ?? this.deriveApiPath(data.urlApi);
        this.apiToken = data.apiToken;
        this.licenseFlowId = data.licenseFlow?.id ?? '';
        this.hostFlowId = data.hostFlow?.id ?? '';
        this.isConfigured = true;

        this.ready$.next();
      },
      error: (error) => {
        this.isConfigured = false;

        if (error?.status === 400 || error?.statusCode === 400) {
          console.warn('Pipedrive ainda não configurado.');
          // Surface a clear, typed error instead of leaving callers hanging forever.
          this.ready$.error(new PipedriveNotConfiguredError());
        } else {
          console.error('Error fetching Pipedrive API URL and token:', error);
          this.ready$.error(error);
        }
      }
    });
  }

  // Full API URL used for Pipedrive REST calls (base + path).
  get urlApi(): string {
    if (!this.baseUrl) {
      return '';
    }

    return `${this.baseUrl.replace(/\/+$/, '')}/${this.apiPath.replace(/^\/+/, '')}`.replace(/\/+$/, '');
  }

  private deriveBaseUrl(urlApi: string): string {
    if (!urlApi) {
      return '';
    }

    try {
      return new URL(urlApi).origin;
    } catch {
      return '';
    }
  }

  private deriveApiPath(urlApi: string): string {
    if (!urlApi) {
      return '';
    }

    try {
      return new URL(urlApi).pathname;
    } catch {
      return '';
    }
  }

  public pipelines(): Observable<any> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => this.http.get(`${this.urlApi}/pipelines`, this.httpOptionsWithToken()))
    );
  }

  public organizationUrl(organizationId: string | number): string {
    if (!organizationId || !this.baseUrl) {
      return '';
    }

    return `${this.baseUrl.replace(/\/+$/, '')}/organization/${organizationId}`;
  }

  public searchOrganizations(term: string): Observable<any> {
    return this.ready$.pipe(
      take(1),
      switchMap(() =>
        this.http.get(`${this.urlApi}/organizations/search?term=${encodeURIComponent(term)}&exact_match=false`, this.httpOptionsWithToken())
      )
    );
  }

  private httpOptionsWithToken() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-token': this.apiToken
      })
    };
  }

}